import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
// Using fetchCoverages service we just added
import { fetchCoverages } from 'services/location_services';
import { getCountryByTimezone } from "helpers/country_helper"
import { getTimezoneFromLocalStorage } from "helpers/datetime_helper"

interface CoverageSelectProps {
    value: string[];
    onChange: (value: string[]) => void;
    selectedCommune?: string;
    disabled?: boolean;
    isFullscreen?: boolean;
}

export const CoverageSelect: React.FC<CoverageSelectProps> = ({
    value,
    onChange,
    selectedCommune,
    disabled = false,
    isFullscreen = false
}) => {
    const [coverages, setCoverages] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const timezone = getTimezoneFromLocalStorage()
    const country = getCountryByTimezone(timezone)

    useEffect(() => {
        const loadCoverages = async () => {
            // Logic from useCoverages hook in mapas:
            // If we have a commune, we use it to filter.
            // If no commune, we might show empty or specific message (handled in render).
            // Here we load data if commune is present.
            // Ensure country is Uppercase if API requires it (common in Omnix)
            const countryParam = country?.toUpperCase() || "CHILE";

            console.log('CoverageSelect: loadCoverages triggered');
            console.log('CoverageSelect: selectedCommune:', selectedCommune);
            console.log('CoverageSelect: countryParam:', countryParam);

            if (!selectedCommune) {
                console.warn('CoverageSelect: No selectedCommune, skipping fetch');
                setCoverages([]);
                return;
            }

            setIsLoading(true);
            setError(null);

            try {
                const userString = localStorage.getItem("USER_SIMO");
                const token = userString ? JSON.parse(userString).token : null;
                console.log('CoverageSelect: Token found?', !!token);

                if (token) {
                    console.log(`Loading coverages for country: ${countryParam}, commune: ${selectedCommune}`);
                    const data = await fetchCoverages(countryParam, selectedCommune, token);
                    console.log('CoverageSelect: Fetched data length:', data.length);
                    setCoverages(data);
                } else {
                    console.error('CoverageSelect: No token found in localStorage');
                }
            } catch (err) {
                console.error("Error loading coverages", err);
                setError("Error cargando coberturas");
            } finally {
                setIsLoading(false);
            }
        };

        loadCoverages();
    }, [selectedCommune, country]);

    // Memoize options
    const options = useMemo(() => {
        return coverages.map(coverage => ({
            value: coverage,
            label: coverage
        }));
    }, [coverages]);

    // Ensure value is array
    // eslint-disable-next-line
    const safeValue = Array.isArray(value) ? value : [];

    // Create selected options for react-select
    const selectedOptions = useMemo(() => {
        return safeValue.map(val => ({
            value: val,
            label: val
        }));
    }, [safeValue]);

    if (error) {
        return <div className="text-red-500 text-sm">Error cargando coberturas</div>;
    }

    if (!selectedCommune) {
        return (
            <div className="text-gray-500 text-xs p-2 border rounded bg-gray-50 border-dashed text-center">
                Seleccione una comuna para ver coberturas
            </div>
        )
    }

    return (
        <div className="coverage-select-container">
            <Select
                isMulti
                value={selectedOptions}
                onChange={(selected) => {
                    const newValue = (selected as unknown as Array<{ value: string }>)?.map(option => option.value) || [];
                    onChange(newValue);
                }}
                options={options}
                isDisabled={disabled || isLoading}
                isLoading={isLoading}
                placeholder="Seleccione..."
                noOptionsMessage={() => "No se encontraron coberturas"}
                classNamePrefix="coverage-select"
                isClearable={true}
                isSearchable={true}
                styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999999 }),
                    menu: (base) => ({ ...base, zIndex: 9999999, fontSize: '12px' }),
                    option: (base) => ({ ...base, fontSize: '12px', padding: '6px 10px' }),
                    control: (base) => ({ ...base, fontSize: '12px', minHeight: '34px' }),
                    valueContainer: (base) => ({ ...base, padding: '2px 8px' }),
                    dropdownIndicator: (base) => ({ ...base, padding: '6px' }),
                    clearIndicator: (base) => ({ ...base, padding: '6px' })
                }}
                menuPortalTarget={isFullscreen ? (document.getElementById('map-portal-root') || document.body) : document.body}
            />
        </div>
    );
};
